/**
 * Team @mentions (TEAM_MENTIONS_FEATURE) — shared by chart notes, task descriptions and task
 * comments. The backend keeps one `Mention` row per (target, person).
 */

/** A teammate the @ picker offers (`GET /mention-candidates/`). */
export interface MentionCandidate {
  id: number;
  /** Only with `include_teams` (TEAMS_FEATURE): "team" rows come first, people are "user". */
  kind?: "user" | "team";
  name: string;
  role?: string;
  clinic_names?: string[];
  /** Works at the clinic the picker was opened for; these are listed first. */
  in_clinic?: boolean;
  /** Team rows only. */
  color?: string;
  alert_mode?: TeamAlertMode;
  member_count?: number;
}

export type TeamAlertMode = "screen" | "bell" | "bell_phone";

export const isTeamCandidate = (candidate: MentionCandidate) =>
  candidate.kind === "team";

export interface MentionCandidatesParams {
  search?: string;
  clinic?: number;
  limit?: number;
  /** TEAMS_FEATURE: list the practice's teams before the people. */
  include_teams?: boolean;
}

/** A team as it appears on a mention row or a task pill. */
export interface MentionTeam {
  id: number;
  name: string;
  color: string;
}

/** A person who was @mentioned and notified, with whether they have opened it since. */
export interface Mention {
  id: number;
  user: { id: number; name: string };
  /** The writer. */
  created_by: number;
  notified_at: string;
  seen_at: string | null;
  /** Set when the person was reached through a team @mention (TEAMS_FEATURE). */
  team?: MentionTeam | null;
  /** The member who took a team mention (the owner of the task it became). */
  taken_by?: { id: number; name: string } | null;
}

/** A person @mentioned in a task comment (`mentioned_users` on the comment). */
export interface MentionedUser {
  id: number;
  name: string;
  /** When this person saw the comment's mention; null while unseen. */
  seen_at?: string | null;
  /** Reached through a team mention (TEAMS_FEATURE). */
  team?: MentionTeam | null;
}

export type MentionKind = "chart_note" | "task" | "task_comment";

/** One row of Home → For me (`GET /mentions/for-me/`, TEAMS_FEATURE). */
export interface MentionForMe {
  id: number;
  kind: MentionKind;
  notified_at: string;
  seen_at: string | null;
  created_by: { id: number; name: string } | null;
  team: MentionTeam | null;
  taken_by: { id: number; name: string } | null;
  /** The follow-up task (a chart-note mention taken or made into a task). */
  task_id: number | null;
  patient: { id: number; name: string } | null;
  snippet: string;
  chart_note_id: number | null;
  staff_task_id: number | null;
  staff_task_title: string | null;
}

export interface MentionsForMeParams {
  kind?: MentionKind;
  /** true: reached me through a team; false: by name. */
  via_team?: boolean;
  /** Team rows nobody has taken yet (true) / taken (false). */
  untaken?: boolean;
  seen?: boolean;
  limit?: number;
  offset?: number;
}

export interface MentionsForMeResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MentionForMe[];
}
